import { BringDataIntoViewControl } from 'app/src/features/Showcase/helpers/BringDataControlHelper'
import { convertFromAtlasPosition, getGeoDistance, round } from 'app/src/features/Showcase/helpers/maputils'
import type { AddressResultType, GeoMapEventType, MapPropsType } from 'app/src/model'
import type { companyagreementApi } from 'app/src/services/welfareportal'
import * as atlas from 'azure-maps-control'
import { Button } from 'primereact/button'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const popupTemplateOptions: atlas.PopupTemplate = {
  content: `<div style="height:100px">{description}</div>`,
  sandboxContent: false,
  singleDescription: false,
  numberFormat: {
    maximumFractionDigits: 2,
  },
}

const popupTemplateOptionsDescriptionLess: atlas.PopupTemplate = {
  ...popupTemplateOptions,
  content: `<div></div>`,
}

const CIRCLE_DATA_SOURCE = 'circleDataSource'
const CURRENTADDRESS_DATA_SOURCE = 'currentAddressDataSource'

const getCurrentLocationRadius = (evt: atlas.MapEvent) => {
  const northEastLong = evt.map.getCamera().bounds?.at(2)

  const mapCenter = evt.map.getCamera().center!
  const mapCenterFirstNumber: number = mapCenter?.at(1) ?? 0
  return round(
    getGeoDistance(
      convertFromAtlasPosition(mapCenter),
      convertFromAtlasPosition(new atlas.data.Position(northEastLong!, mapCenterFirstNumber)),
    ),
  )
}

const removeCircle = (_map: atlas.Map) => {
  if (_map.sources.getById(CIRCLE_DATA_SOURCE)) {
    ;(_map.sources.getById(CIRCLE_DATA_SOURCE) as atlas.source.DataSource).clear()
  }
}

const addCircle = (geoEvent: GeoMapEventType, maxLocationRadius: number) => {
  let circleDataSource: atlas.source.DataSource
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((geoEvent.evt.map as any).ready) {
    if (!geoEvent.evt.map.sources.getById(CIRCLE_DATA_SOURCE)) {
      circleDataSource = new atlas.source.DataSource(CIRCLE_DATA_SOURCE)
      geoEvent.evt.map.sources.add(circleDataSource)
      geoEvent.evt.map.layers.add(
        new atlas.layer.PolygonLayer(circleDataSource, 'circleLayer', {
          fillColor: 'rgba(0, 200, 200, 0.4)',
        }),
      )
    } else {
      circleDataSource = geoEvent.evt.map.sources.getById(CIRCLE_DATA_SOURCE) as atlas.source.DataSource
      circleDataSource.clear()
    }

    //Create a circle
    circleDataSource.add(
      new atlas.data.Feature(new atlas.data.Point(geoEvent.evt.map.getCamera().center!), {
        subType: 'Circle',
        radius: geoEvent.locationRadius
          ? geoEvent.locationRadius > maxLocationRadius
            ? maxLocationRadius
            : geoEvent.locationRadius
          : maxLocationRadius,
      }),
    )
  }
}

export function GeoMap(props: MapPropsType) {
  const [position, setPosition] = useState<AddressResultType & { defaultPositionSeek: boolean }>()

  const [map, setMap] = useState<atlas.Map>()
  const [hidden, setHidden] = useState<boolean>(false)
  const [showIcon, setShowIcon] = useState<boolean>(false)
  const [mapReady, setMapReady] = useState<boolean>(false)

  const [circleEvent, setCircleEvent] = useState<GeoMapEventType>()

  const translation = useTranslation('catalog', { keyPrefix: 'Catalog.Showcase' })

  useEffect(() => {
    if (hidden) {
      setTimeout(() => {
        setShowIcon(true)
      }, 500)
    } else {
      setShowIcon(false)
    }
  }, [hidden])

  useEffect(() => {
    window?.addEventListener('resize', () => {
      setTimeout(() => {
        map?.resize()
      }, 100)
    })
  }, [map])

  useEffect(() => {
    if (props?.address?.position) setPosition({ ...props.address, defaultPositionSeek: false })
    else if (props.defaultPosition)
      setPosition({ ...{ position: convertFromAtlasPosition(props.defaultPosition) }, defaultPositionSeek: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.address?.position?.lat, props?.address?.position?.lon])

  useEffect(() => {
    if (props?.defaultPosition && !props?.address?.position)
      setPosition({ ...{ position: convertFromAtlasPosition(props.defaultPosition) }, defaultPositionSeek: true })
  }, [props?.address?.position, props.defaultPosition])

  useEffect(() => {
    if (!map) return
    removeCircle(map!)
    if (
      typeof props.mapConfig?.displayCircle !== 'undefined' &&
      props.mapConfig.displayCircle &&
      circleEvent?.zoom &&
      circleEvent.zoom < props.mapConfig?.atlasMaxZoomCircle &&
      !props?.address?.position &&
      props.mapConfig?.maxLocationRadius
    ) {
      addCircle(circleEvent, props.mapConfig?.maxLocationRadius)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleEvent, props.mapConfig, props?.address?.position])

  useEffect(() => {
    if (!map) {
      const instMap = new atlas.Map('map', {
        view: 'Auto',
        language: translation.i18n?.language ?? 'it-IT',
        showFeedbackLink: false,
        showLogo: false,
        center: props.defaultPosition,
        zoom: props.mapConfig?.atlasInitialFreeZoom,
        authOptions: props?.authOptions,
      })

      instMap.controls.add(
        [
          new atlas.control.ZoomControl(),
          new BringDataIntoViewControl({
            units: 'imperial',
            defaultZoom: props.mapConfig?.atlasInitialFreeZoom,
            onControlClickCallBack: props.positionRestoreCallBack,
            i18n: {
              restorePosition: translation.t('Map.restorePositionLabel'),
            },
          }),
        ],
        {
          position: atlas.ControlPosition.TopRight,
          open: atlas.ControlPosition.TopRight,
        },
      )

      const zoomDragHandler = (evt: atlas.MapEvent) => {
        const geoMapEvent = {
          evt,
          locationRadius: getCurrentLocationRadius(evt),
          zoom: evt.map.getCamera().zoom,
        }
        if (props.onMapEvent) props.onMapEvent(geoMapEvent)

        setCircleEvent(geoMapEvent)
      }
      instMap.events.add('zoomend', zoomDragHandler)
      instMap.events.add('dragend', zoomDragHandler)

      instMap.events.add('ready', (evt: atlas.MapEvent) => {
        props.onMapEvent?.({
          evt,
          locationRadius: getCurrentLocationRadius(evt),
          zoom: evt.map.getCamera().zoom,
        })

        setMapReady(true)
      })

      setMap(instMap)
    } else {
      if (position && position?.position?.lon && position?.position?.lat) {
        map.setCamera({
          center: new atlas.data.Position(position.position.lon, position.position.lat),
          zoom: position.address?.freeformAddress
            ? props.mapConfig?.atlasInitialAddressZoom
            : props.mapConfig?.atlasInitialFreeZoom,
        })
        if (map.sources.getById(CURRENTADDRESS_DATA_SOURCE) && !position.defaultPositionSeek) {
          const currentAddressDS = map.sources.getById(CURRENTADDRESS_DATA_SOURCE) as atlas.source.DataSource
          currentAddressDS.clear()
          if (position.position && position.address?.freeformAddress) {
            const atlasPosition = new atlas.data.Position(position.position.lon, position.position.lat)
            currentAddressDS.add(new atlas.data.Feature(new atlas.data.Point(atlasPosition)))
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position])

  useEffect(() => {
    if (map && mapReady) {
      if (map.popups.getPopups()?.length > 0) map.popups.getPopups()[0].close()

      let _dataSource: atlas.source.DataSource
      if (!map.sources.getById('productDataSource')) {
        _dataSource = new atlas.source.DataSource('productDataSource', { cluster: true, clusterRadius: 50, clusterMaxZoom: 15 })
        map.sources.add(_dataSource)

        const clusterBubbleLayer = new atlas.layer.BubbleLayer(_dataSource, 'productBubbleLayer', {
          radius: [
            'step',
            ['get', 'point_count'],
            20, //Default of 20 pixel radius.
            100,
            30, //If point_count >= 100, radius is 30 pixels.
            750,
            40, //If point_count >= 750, radius is 40 pixels.
          ],
          strokeColor: 'rgb(0, 71, 187)',
          strokeWidth: 0,
          color: [
            'step',
            ['get', 'point_count'],
            'rgb(0, 71, 187)',
            100,
            'rgb(0, 71, 187)', //If the point_count >= 100, color is yellow.
            750,
            'rgba(255,0,0,0.8)',
          ],
          filter: ['has', 'point_count'],
        })

        const currentAddressDataSource = new atlas.source.DataSource(CURRENTADDRESS_DATA_SOURCE)
        map.sources.add(currentAddressDataSource)
        const currentAddressDataLayer = new atlas.layer.SymbolLayer(currentAddressDataSource, 'currentAddressDataLayer', {
          iconOptions: {
            image: 'pin-round-red',
          },
        })

        map.layers.add([
          currentAddressDataLayer,
          clusterBubbleLayer,

          new atlas.layer.SymbolLayer(_dataSource, 'productSymbolLayerCount', {
            iconOptions: {
              image: 'none', //Hide the icon image.
            },
            textOptions: {
              textField: ['get', 'point_count'],
              color: 'white',
              offset: [0, 0.4],
            },
          }),
          new atlas.layer.SymbolLayer(_dataSource, 'productSymbolLayerSingleLocations', {
            filter: ['!', ['has', 'point_count']],
          }),
        ])

        const popup_ = new atlas.Popup({
          pixelOffset: [0, -18],
          closeButton: true,
        })
        map.popups.add(popup_)
      } else {
        _dataSource = map.sources.getById('productDataSource') as atlas.source.DataSource
      }
      _dataSource.clear()

      if (props.productGridList?.length > 0) {
        props.productGridList.forEach((product: companyagreementApi.ProductListItem) => {
          if (product.geoLocation) {
            const atlasPosition = new atlas.data.Position(product.geoLocation.longitude, product.geoLocation.latitude)

            const feature = new atlas.data.Feature(new atlas.data.Point(atlasPosition), {
              ...product,
              title: product.productName,
            })
            _dataSource.add(feature)
          }
        })

        const popupOpenFunc = (evt: atlas.MapMouseEvent) => {
          const popupContent = document.createElement('div')
          const featureProperties = (evt.shapes?.[0] as { data?: { properties: { geoAddress: string; description: string } } })
            ?.data?.properties
          if (!featureProperties) return
          if (featureProperties.description)
            popupContent.appendChild(atlas.PopupTemplate.applyTemplate(featureProperties, popupTemplateOptions))
          else popupContent.appendChild(atlas.PopupTemplate.applyTemplate(featureProperties, popupTemplateOptionsDescriptionLess))
          const button = document.createElement('button')
          const buttonSpan = document.createElement('span')
          button.appendChild(buttonSpan)
          buttonSpan.innerText = translation.t('Map.openProductLabel')
          button.setAttribute(
            'style',
            'margin:0 auto 10px auto;cursor:pointer;height:30px;width:100px;color:var(--primary-color-text);background:var(--primary-color);border-radius:30px;',
          )
          button.addEventListener('click', () => props.onProductClick(featureProperties))
          const buttonDiv = document.createElement('div')
          buttonDiv.setAttribute('style', 'display:flex;margin-top:5px')
          buttonDiv.appendChild(button)

          const addressDiv = document.createElement('div')
          const addressSpan = document.createElement('span')
          addressDiv.appendChild(addressSpan)
          addressSpan.innerText = featureProperties.geoAddress

          const popupContentInnerDiv = document.createElement('div')
          popupContentInnerDiv.setAttribute('style', 'padding:5px 5px 5px 10px')
          popupContentInnerDiv.appendChild(addressDiv)
          popupContent.appendChild(popupContentInnerDiv)
          popupContent.appendChild(buttonDiv)
          const popup = map.popups.getPopups()[0]
          popup.setOptions({
            position: evt.position,
            content: popupContent,
          })
          popup.open(map)
        }
        map.events.add('click', map.layers.getLayerById('productSymbolLayerSingleLocations'), popupOpenFunc)
      }
    }
  }, [props, mapReady, map, translation])

  return (
    <div
      style={{
        position: !hidden ? 'relative' : undefined,
        minHeight: '50px',
        height: hidden ? '0px' : '400px',
      }}
    >
      {showIcon && (
        <div
          className='absolute ml-[6px] flex cursor-pointer items-center duration-1000'
          style={{ opacity: 1 }}
          onClick={() => setHidden(!hidden)}
        >
          <Button aria-expanded={false} aria-label={translation.t('Map.showMapLabel')} onClick={() => {}}>
            {translation.t('Map.showMapLabel')}
          </Button>
        </div>
      )}

      <div
        id='map'
        className='block duration-1000'
        style={{
          position: hidden ? 'relative' : undefined,
          height: hidden ? '0px' : '400px',
          opacity: hidden ? 0 : 1,
        }}
      >
        <div className='absolute left-[6px]' style={{ top: hidden ? 0 : '10px' }}>
          <Button
            className={'mapNavButton'}
            aria-expanded
            aria-label={translation.t('Map.hideMapLabel')}
            tooltip={translation.t('Map.hideMapLabel')}
            onClick={() => setHidden(!hidden)}
            icon='pi-chevron-up pi'
          ></Button>
        </div>
      </div>
    </div>
  )
}
